/**
 * Webhook Management Hooks
 * TanStack Query hooks for webhook operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as webhooksService from '@/services/webhooks.service';
import type { CreateWebhookRequest } from '@/types/api.types';

/**
 * Admin: List all webhooks
 */
export const useWebhooks = () => {
  return useQuery({
    queryKey: ['webhooks-admin'],
    queryFn: () => webhooksService.listWebhooks(),
    staleTime: 10 * 1000,
  });
};

/**
 * Admin: Create webhook
 */
export const useCreateWebhook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWebhookRequest) => webhooksService.createWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks-admin'] });
    },
  });
};

/**
 * Admin: Update webhook
 */
export const useUpdateWebhook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ webhookId, data }: { webhookId: string; data: Partial<CreateWebhookRequest> }) =>
      webhooksService.updateWebhook(webhookId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks-admin'] });
    },
  });
};

/**
 * Admin: Delete webhook
 */
export const useDeleteWebhook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (webhookId: string) => webhooksService.deleteWebhook(webhookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks-admin'] });
    },
  });
};

/**
 * Admin: Get webhook logs
 */
export const useWebhookLogs = (webhookId: string | null, limit = 50) => {
  return useQuery({
    queryKey: ['webhook-logs', webhookId, limit],
    queryFn: () => webhooksService.getWebhookLogs(webhookId!, limit),
    enabled: !!webhookId,
    staleTime: 5 * 1000,
  });
};
